import React, { useMemo } from "react";
import { useDataWithRoute } from "../utils/hooks";
import { VsiBubble } from "../styled/App.styled";
import Stockchart from "../components/Stockchart";
import { formatStockChartConfig } from "../utils/Helpers";
import { iopsLineColor, latencyLineColor, mbsecLineColor, usedLineColor } from "../utils/chartUtils";
import { Spinner } from "reactstrap";
import { useVsiAppContext } from "../stores/VsiApp";


const performanceSeriesLines = [
  { name: "IOPS", field: "iosec", value: "iops", dashStyle: "solid" },
  { name: "IOPS Compressed", field: "compressed_iops", value: "iops", dashStyle: "DashDot" },
  { name: "Average Latency", field: "average_latency", value: "latency" },
  { name: "Read Latency", field: "average_read_latency", value: "latency", dashStyle: "DashDot" },
  { name: "Latency Compressed", field: "compressed_latency", value: "latency", dashStyle: "Dot" },
  { name: "Mb/Sec", field: "mbsec", value: "mbsec" },
  { name: "Used", field: "used", value: "used" }
];

const performanceTypes = [
  { value: "iops", label: "IOPS", color: iopsLineColor },
  { value: "latency", label: "Latency (ms)", color: latencyLineColor },
  { value: "mbsec", label: "Mb/Sec", color: mbsecLineColor },
  { value: "used", label: "Used (GiB)", color: usedLineColor }
];

export default function HostTrends({ params }) {
  const { client: { clientId } } = useVsiAppContext();

  const { searchHost, searchDevice, searchAxis1, searchAxis2 } = useMemo(() => {
    let searchHost;
    let searchDevice;
    let searchAxis1;
    let searchAxis2;

    for (let i = 0; i < params?.length; i++) {
      if (params?.[i].param === "host_name") {
        searchHost = params?.[i].value;
      } else if (params?.[i].param === "device_name") {
        searchDevice = params?.[i].value;
      } else if (params?.[i].param === "axis_1") {
        searchAxis1 = params?.[i].value;
      } else if (params?.[i].param === "axis_2") {
        searchAxis2 = params?.[i].value;
      }
    }
    return {
      searchHost,
      searchDevice,
      searchAxis1,
      searchAxis2
    };
  }, [params]);

  const defaultAxis1 = performanceTypes.find(({ value }) => searchAxis1 === value) || performanceTypes[0];
  const defaultAxis2 = performanceTypes.find(({ value }) => searchAxis2 === value) || performanceTypes[1];

  const { value: axis1Val, label: axis1Label, color: axis1Color } = defaultAxis1;
  const { value: axis2Val, label: axis2Label, color: axis2Color } = defaultAxis2;

  const route = useMemo(() => `/api/v6/report/host_iops_latency_proc?client_id=${clientId}&host_name=${searchHost}&device_name=${searchDevice}`, [clientId, searchHost, searchDevice]);
  const [{ recordset }, { isLoading }] = useDataWithRoute(route);

  const { chartConfig, formattedChartData } = useMemo(() => {
    const yAxis1 = [];
    const yAxis2 = [];
    const axis1ColorsMunged = [];
    const axis2ColorsMunged = [];

    performanceSeriesLines.forEach((line) => {
      if (line.value === axis1Val) {
        yAxis1.push({
          name: line.name,
          field: line.field,
          dashStyle: line.dashStyle
        });
        axis1ColorsMunged.push(axis1Color);
      } else if (line.value === axis2Val) {
        yAxis2.push({
          name: line.name,
          field: line.field,
          dashStyle: line.dashStyle,
          yAxis: 1
        });
        axis2ColorsMunged.push(axis2Color);
      }
    });

    const config = formatStockChartConfig(recordset, [...yAxis1, ...yAxis2]);
    return {
      formattedChartData: config.series,
      chartConfig: {
        yAxis: [
          {
            title: {
              text: axis1Label
            }
          },
          {
            title: {
              text: axis2Label
            },
            opposite: true
          }
        ],
        colors: [...axis1ColorsMunged, ...axis2ColorsMunged]
      }
    };
  }, [recordset, axis1Label, axis2Label, axis1Val, axis2Val, axis1Color, axis2Color]);

  return isLoading ? (
    <Spinner color={"success"} />
  ) : (
    <VsiBubble>
      <Stockchart series={formattedChartData}
                  config={chartConfig}
                  height={420}
                  navDisabled={true} />
    </VsiBubble>
  );

}